import React from 'react';

import { Box, Skeleton, Grid } from '@mui/material';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';

import { RepaymentSchedule } from '@clients/domains/lending/lending';
import DataVisualizationEntries from '@components/overviewPanels/loanRepaymentsPanel/DataVisualizationEntries';

enum ExpandedSection {
    ALL = 0,
    START,
    MIDDLE,
    END,
}

export interface DataVisualizationProps {
    isLoading?: boolean;
    repaymentSchedule?: RepaymentSchedule[];
    currentPaymentIndex: number;
}

const DataVisualization = ({
    isLoading = false,
    repaymentSchedule,
    currentPaymentIndex,
}: DataVisualizationProps) => {
    const { theme } = useTheme();
    const compressThreshold = 25;
    const individualEntriesToShowWhenCompressed = Math.ceil(
        compressThreshold / 2,
    );

    if (isLoading) {
        return (
            <Skeleton
                variant="rounded"
                height={theme.spacing(7)}
                data-testid="repayments-to-make-bar-skeleton"
            />
        );
    }
    if (!repaymentSchedule?.length) {
        return (
            <Box
                width="100%"
                bgcolor="grey.50"
                height={theme.spacing(7.125)}
                borderRadius={0.25}
                data-testid="no-repayments-to-make-bar"
            />
        );
    }
    if (repaymentSchedule?.length >= 100) {
        let sectionToExpand: ExpandedSection;
        let individualRepaymentsToShow: RepaymentSchedule[];
        if (currentPaymentIndex <= compressThreshold) {
            sectionToExpand = ExpandedSection.START;
            individualRepaymentsToShow = repaymentSchedule.slice(
                0,
                compressThreshold,
            );
        } else if (
            currentPaymentIndex >=
            repaymentSchedule?.length - compressThreshold
        ) {
            sectionToExpand = ExpandedSection.END;
            individualRepaymentsToShow = repaymentSchedule.slice(
                repaymentSchedule?.length - compressThreshold,
                repaymentSchedule.length,
            );
        } else {
            sectionToExpand = ExpandedSection.MIDDLE;
            const startSlice = Math.max(
                0,
                Math.ceil(
                    currentPaymentIndex - individualEntriesToShowWhenCompressed,
                ),
            );
            const endSlice = Math.min(
                repaymentSchedule.length,
                Math.floor(
                    currentPaymentIndex + individualEntriesToShowWhenCompressed,
                ),
            );
            individualRepaymentsToShow = [
                ...repaymentSchedule.slice(startSlice, endSlice),
            ];
        }
        return (
            <Grid container flexDirection="row" height="100%">
                {currentPaymentIndex > compressThreshold && (
                    <Grid
                        item
                        display="flex"
                        height="100%"
                        xs={sectionToExpand === ExpandedSection.END ? 7 : 3.5}>
                        <Box
                            mr={0.25}
                            bgcolor="primary.main"
                            borderRadius={0.25}
                            height="100%"
                            width="100%"
                            data-testid="previous-repayments-compressed-block"
                        />
                    </Grid>
                )}
                <Grid item display="flex" xs={5} height="100%">
                    <DataVisualizationEntries
                        repaymentSchedule={individualRepaymentsToShow}
                    />
                </Grid>
                {currentPaymentIndex <
                    repaymentSchedule.length - compressThreshold && (
                    <Grid
                        item
                        display="flex"
                        height="100%"
                        xs={
                            sectionToExpand === ExpandedSection.START ? 7 : 3.5
                        }>
                        <Box
                            ml={0.25}
                            bgcolor="grey.50"
                            borderRadius={0.25}
                            height="100%"
                            width="100%"
                            data-testid="next-repayments-compressed-block"
                        />
                    </Grid>
                )}
            </Grid>
        );
    } else {
        return (
            <DataVisualizationEntries repaymentSchedule={repaymentSchedule} />
        );
    }
};

export default DataVisualization;
